import React, { FC, lazy, memo, ReactElement, Suspense, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { CampaignOverlayDummyContent } from 'src/components/overlays/Campaign/DummyContent';
import { TeamOrMemberOverlayDummyContent } from 'src/components/overlays/TeamOrMember/DummyContent';
import { UiLoader } from 'src/components/UI/loaders';
import { dispatchOverlaySliceShow } from 'src/store/overlay/helpers';
import { overlaySelector } from 'src/store/overlay/selectors';
import { overlaySlice, OverlaySliceStateType } from 'src/store/overlay/slice';
import { Overlay } from 'src/components/common/Overlay';
import { getSearchParams } from 'src/utils/url';
import { configurationLoadedSelector } from 'src/store/commonPart/selector';

const DonorProfileOverlay = lazy(() => import('src/components/overlays/DonorProfile/index'));
const CampaignOverlay = lazy(() => import('src/components/overlays/Campaign/index'));
const TeamOrMemberOverlay = lazy(() => import('src/components/overlays/TeamOrMember/index'));

const Component: FC = memo(
  () => {
    const dispatch = useDispatch();

    const overlay = useSelector(overlaySelector);

    const { search } = useLocation();

    useEffect(() => {
      if (!overlay.show) {
        const typesByIndex: NonNullable<OverlaySliceStateType['type']>[] = [
          'campaign',
          'teams',
          'members',
          'donorProfile',
        ];

        const openedOverlay = getSearchParams([
          'openedCampaignId',
          'openedTeamId',
          'openedTeamMemberId',
          'openedDonorProfileId',
        ])
          .map((id, index) => ({ id: +(id || 0), type: typesByIndex[index] }))
          .find(({ id }) => !!id);

        if (openedOverlay) {
          dispatchOverlaySliceShow({ id: openedOverlay.id, type: openedOverlay.type });
        }
      }
    }, [overlay.show, search]);

    const handleClose = useCallback(() => {
      dispatch(overlaySlice.actions.close());
    }, [dispatch]);

    const searchParam = useMemo(() => {
      switch (overlay.type) {
        case 'teams':
          return 'openedTeamId';
        case 'members':
          return 'openedTeamMemberId';
        case 'donorProfile':
          return 'openedDonorProfileId';
        default:
          return 'openedCampaignId';
      }
    }, [overlay.type]);

    const { content, containerId } = useMemo(() => {
      let content: (onClose: () => void) => ReactElement | null = () => null;
      let containerId;

      switch (overlay.type) {
        case 'donorProfile':
          content = (onClose) => (
            <Suspense fallback={<UiLoader fullScreenCentered />}>
              <DonorProfileOverlay onClose={onClose} id={overlay.id} />
            </Suspense>
          );
          containerId = 'organization-donor-profile-overlay-container';
          break;
        case 'campaign':
          content = (onClose) => (
            <Suspense fallback={<CampaignOverlayDummyContent />}>
              <CampaignOverlay onClose={onClose} id={overlay.id} />
            </Suspense>
          );
          break;
        case 'teams':
        case 'members':
          content = (onClose) => (
            <Suspense fallback={<TeamOrMemberOverlayDummyContent />}>
              <TeamOrMemberOverlay
                onClose={onClose}
                id={overlay.id}
                type={overlay.type as 'teams' | 'members'}
                reloadTableCallback={overlay.reloadTableCallback}
              />
            </Suspense>
          );
          containerId = 'dashboard-layout-container';
          break;
        default:
          content = () => null;
      }

      return {
        content,
        containerId,
      };
    }, [overlay]);

    return (
      <Overlay
        containerId={containerId}
        show={overlay.show}
        id={overlay.id}
        key={overlay.key}
        searchParam={overlay.noSearchParam ? undefined : searchParam}
        onClose={handleClose}
      >
        {content}
      </Overlay>
    );
  },
  () => true
);

export const CommonOverlay: FC = memo(
  () => (useSelector(configurationLoadedSelector) ? <Component /> : null),
  () => true
);
